import {
    Component,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import {ToolService} from "app/core/services/tool.service";
import {ActivatedRoute, Router} from "@angular/router";
import {ToastsManager} from "@app/shared/util/toastr.service";
import {TranslateParser, TranslateService} from "@ngx-translate/core";
import {Tool} from "@app/tool/model/tool.model";
import {Log} from "ng2-logger";
import {ToolDatamatrix} from "@app/tool/model/toolDatamatrix.model";
import {Subscription} from "rxjs";
import {ToolEngraveModalComponent} from "@app/tool/components/tool-engrave-modal/tool-engrave-modal.component";
import {AuthenticationService} from "@app/authentication/services/authentication.service";
import {PermissionService} from "@app/authentication/services/permission.service";
import {ToolDetails} from "@app/tool/model/toolDetails.model";
import {TreeModel} from "@components/tree/tree.model";
import {IssuesService} from "@services/issues.service";
import {UntypedFormBuilder, UntypedFormControl, UntypedFormGroup} from "@angular/forms";
import {TicketService} from "@services/ticket.service";
import {ToolReturnsInspectComponent} from "@app/roles/atom-operator/pages/tool-returns";
import {ToolBackupModalComponent} from "@app/tool/details/components";
import {IssueModalComponent, OperationsForReturnModalComponent, TicketPromoteModalComponent} from "@app/tool/interventions/components";
import {Issue} from "@models/issue.model";
import {Ticket} from "@models/ticket.model";
import {ToolResetModalComponent} from "@app/tool/details/components/modals/tool-reset-modal/tool-reset-modal.component";
import {ConfirmDeleteModalComponent} from "@app/tool/components/confirm-delete-modal/confirm-delete-modal.component";
import {ConfirmationModalComponent} from '@app/shared/components';
import {OperationType} from '../model/operationType.model';
import {TicketType} from "@models/enums/ticket-type.enum";
import {MaintenancePlanDetailsComponent} from '@app/maintenance/containers/maintenance-plan-details/maintenance-plan-details.component';
import {Role} from "@models/roles.enum";

@Component({
    selector: 'app-details',
    templateUrl: './details.component.html',
    styleUrls: ['./details.component.scss']
})
export class DetailsComponent implements OnInit, OnDestroy, OnChanges {
    _logger = Log.create(DetailsComponent.name);

    @ViewChild(ToolEngraveModalComponent) engraveModal: ToolEngraveModalComponent;
    @ViewChild('resetConfirmation') resetConfirmationModal: ConfirmationModalComponent;
    @ViewChild(ToolResetModalComponent) resetToolConfirmationModal: ToolResetModalComponent;
    @ViewChild(ToolBackupModalComponent) toolBackupModal: ToolBackupModalComponent;
    @ViewChild(TicketPromoteModalComponent) ticketPromoteModal: TicketPromoteModalComponent;
    @ViewChild(IssueModalComponent) issueModal: IssueModalComponent;
    @ViewChild(ConfirmDeleteModalComponent) deleteToolConfirmationModal: ConfirmDeleteModalComponent;
    @ViewChild(OperationsForReturnModalComponent) operationsForReturnModalComponent: OperationsForReturnModalComponent;
    @ViewChild('invalidateConfirmation') invalidateConfirmationModal: ConfirmationModalComponent;
    @ViewChild('revalidateConfirmation') revalidateConfirmationModal: ConfirmationModalComponent;
    @ViewChild('revalidateScanBadge') revalidateScanBadgeModal: ConfirmationModalComponent;
    @ViewChild('invalidateScanBadge') invalidateScanBadgeModal: ConfirmationModalComponent;
    @ViewChild('errorModal') errorModal: ConfirmationModalComponent;

    confirmationPopupMessage: string;

    selectedToolId: number;
    selectedTool: Tool;

    // TODO this shouldn't be used. We should find a cleaner way of handling the tool scenario
    isToolScanned = false;

    selectedToolDetails: ToolDetails;

    // The Datamatrixes needed for the engrave operation
    toolDatamatrix: ToolDatamatrix[];

    // The tools that the selectedTool can be replaced with
    toolBackups: Tool[] = [];

    resetForm: UntypedFormGroup;

    toolSelectionSubscription: Subscription;
    toolDetailsUpdateSubscription: Subscription;
    showValidations = false;

    activeComponentRef: any;

    reason: string;

    scannedTools = [];

    loading = false;
    requestInProgress = false;
    inUseActions = ''
    issue: Issue;
    tabs = [
        {
            link: 'details',
            label: 'admin.tools.tabs.detailsTab'
        },
        {
            link: 'documents',
            label: 'admin.tools.tabs.documentsTab'
        },
        {
            link: 'interventions',
            label: 'admin.tools.tabs.interventionHistoryTab'
        },
        {
            link: 'maintenance-plan',
            label: 'admin.tools.tabs.maintenancePlan'
        },
        {
            link: 'strain-measurements',
            label: 'admin.tools.tabs.strainMeasurements'
        },
    ]

    fabItems = [];

    static scannedToolsValidator(control: UntypedFormControl): { [s: string]: boolean } {
        if (control.value.length < ToolReturnsInspectComponent.returnTools.length || !control.value.length) {
            return {'allToolsInspected': true};
        }
        return null;
    }

    constructor(private route: ActivatedRoute,
                private toolService: ToolService,
                private translate: TranslateService,
                private router: Router,
                private ticketService: TicketService,
                private authenticationService: AuthenticationService,
                private permissionService: PermissionService,
                private issueService: IssuesService,
                private formBuilder: UntypedFormBuilder,
                private translationService: TranslateService,
                private translateParser: TranslateParser,
                private toastr: ToastsManager) {
    }

    ngOnInit() {
        this.route.params.subscribe(params => {
            this.selectedToolId = +params['id'];
            this.getToolById(this.selectedToolId);
        });

        this._logger.info('ngOnInit:: Subscribing to toolSelectedAsObservable');
        this.toolSelectionSubscription = this.toolService.toolSelectedAsObservable.subscribe((tool: Tool) => {
            if (tool && tool.id) {
                this.getToolDatamatrix(tool.id);
            }
        });

        this.toolDetailsUpdateSubscription = this.toolService.toolUpdateObservable.subscribe((tool: Tool) => {
            if (tool && tool.id) {
                this.getToolById(this.selectedToolId);
                this.getToolDetails(this.selectedToolId);
            }
        });

        this.onFormInit();
    }

    onFormInit() {
        this.resetForm = this.formBuilder.group({
            scannedTool: [[], DetailsComponent.scannedToolsValidator.bind(this)]
        });
        this._logger.info('onFormInit:: Form initialised', this.resetForm);
    }

    onActivate(componentRef) {
        this.activeComponentRef = componentRef;
        if (componentRef && componentRef instanceof MaintenancePlanDetailsComponent) {
            if (this.selectedToolDetails) {
                componentRef.setToolDetails(this.selectedToolDetails);
                componentRef.setTool(this.selectedTool);
            } else {
                setTimeout(() => this.getToolDetails(this.selectedToolId));
            }
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['selectedTool'] && this.selectedTool && this.resetForm) {
            this.resetForm.setValue({
                scannedTool: this.selectedTool
            })
        }
    }

    ngOnDestroy(): void {
        this.toolService.announceToolSelection(null);
        this.toolService.announceToolUpdate(null);

        if (this.toolSelectionSubscription) {
            this.toolSelectionSubscription.unsubscribe();
        }

        if (this.toolDetailsUpdateSubscription) {
            this.toolDetailsUpdateSubscription.unsubscribe();
        }

    }


    onConfirmDeleteTool(deleteConfirmation: any) {
        this._logger.info('deleteTool:: Deleting ', this.selectedTool, deleteConfirmation);
        this.toolService.deleteTool(deleteConfirmation).subscribe(
            (data: any) => {
                this._logger.info('deleteTool:: Success ', data);
                if (data.message) {
                    this.toastr.info(this.translationService.instant(data.message));
                    this.toolService.announceToolSelection(null);
                    this.router.navigate(['/tool-management']);
                } else {
                    this.toastr.success(this.buildDeleteToolSuccessMessage());
                    this.toolService.announceToolSelection(null);
                    this.router.navigate(['/tool-management']);
                }
                this.deleteToolConfirmationModal.close();
            }
        );
    }

    onPromoteToTicket(issue: Issue) {
        this._logger.info("onPromoteToTicket:: Issue to be promoted", issue);
        this.issue = issue;
        this.loading = true;
        this.ticketService.getOperationsForReturnOrIssueTools(0, issue).subscribe(
            data => {
                this.issueModal.close();
                if (data.length > 0) {
                    this.operationsForReturnModalComponent.open(data, TicketType.Issue);
                } else {
                    this.ticketPromoteModal.open(issue);
                }
                this.loading = false;
            },
            error => {
                this._logger.error("getOperationsForReturnOrIssueTools:: API returned error", error);
                this.loading = false;
            }
        );
    }


    onOperationInfoApproved(operations: OperationType[]) {
        this.ticketPromoteModal.open(this.issue, operations);
    }

    createTicket(ticket: Ticket) {
        this._logger.info("createTicket:: Creating ticket", ticket);
        this.ticketService.createTicket(ticket).subscribe(
            (data: any) => {
                this._logger.info("createTicket:: API returned data", data);
                this.toastr.success(this.buildCreateMessage(data));
                this.getToolById(this.selectedTool.id);
                this.ticketPromoteModal.close();
            },
            (error: any) => {
                if (error && error.error) {
                    this.confirmationPopupMessage = error.error.message;
                    this.errorModal.open();
                }
                this._logger.error("createTicket:: API returned error", error);
            })
    }

    onReject(issue: Issue) {
        this._logger.info("onReject:: Issue to be rejected", issue);
        this.rejectIssue(issue);
    }

    rejectIssue(issue: Issue) {
        this.issueModal.close();
        this._logger.info("rejectIssue:: Rejecting issue", issue);
        this.issueService.rejectIssue(issue).subscribe(
            (data) => {
                this._logger.info("rejectIssue:: API returned data", data);
                this.toastr.success(this.translationService.instant('atom.tool-returns.toasts.rejectSuccess'));
                this.getToolById(this.selectedTool.id);
            },
            (error) => {
                this._logger.info("rejectIssue:: API returned error", error);
                this.toastr.error(this.translationService.instant('atom.tool-returns.toasts.rejectFail'));
            }
        )
    }

    getToolById(id: number) {
        this._logger.info('getToolById:: Retrieving tool by id', id);
        this.loading = true;
        this.toolService.getToolById(id).subscribe(
            (data: any) => {
                this._logger.info('getToolById:: API returned data', data);
                this.selectedTool = data;
                this.scannedTools = [this.selectedTool];
                this.selectedTool.isScanned = this.isToolScanned;
                this.toolService.announceToolSelection(this.selectedTool);
                this.loading = false;
            }, (error: any) => {
                this._logger.error('getToolById:: API returned error', error);
                this.loading = false;
            });
    }

    getToolDetails(id: number) {
        this._logger.info('getToolById:: Retrieving tool details by id', id);
        this.loading = true;
        this.toolService.getDetailsForTool(id).subscribe(
            (data: any) => {
                this._logger.info('getToolDetails:: API returned data', data);
                this.selectedToolDetails = data;
                if (this.activeComponentRef instanceof MaintenancePlanDetailsComponent) {
                    this.activeComponentRef.setToolDetails(this.selectedToolDetails);
                    this.activeComponentRef.setTool(this.selectedTool);
                }
                this.loading = false;
            }, (error: any) => {
                this._logger.error('getToolDetails:: API returned error', error);
                this.loading = false;
            });
    }


    getToolBackups(id: number) {
        this._logger.info('getToolBackups:: Retrieving backups for toolId=', id);
        this.requestInProgress = true;
        this.toolService.getToolBackups(id).subscribe(data => {
            this._logger.info('getToolBackups:: API returned data', data);
            this.toolBackups = data;
            this.requestInProgress = false;
        });
    }

    invalidateTool(tool: Tool) {
        this.invalidateConfirmationModal.open(tool);
    }

    onSubmitInvalidationReason(reason: any) {
        this.invalidateConfirmationModal.close();
        this.reason = reason;

        this.invalidateScanBadgeModal.open();
    }

    onConfirmScanBadge(event: any) {
        this.confirmInvalidateTool(this.reason, event.boschId);
    }

    revalidateTool(tool: Tool) {
        this.revalidateConfirmationModal.open(tool);
    }

    onSubmitRevalidationReason(reason: any) {
        this.revalidateConfirmationModal.close();
        this.reason = reason;

        this.revalidateScanBadgeModal.open();
    }

    onConfirmRevalidateScanBadge(event: any) {
        this.confirmRevalidateTool(this.selectedTool, this.reason, event.boschId);
    }

    confirmRevalidateTool(tool: Tool, reason: string, boschId: string) {
        this._logger.info('revalidateTool:: Making tool valid', tool);
        tool.isAvailableForManufactoring = true;
        this.toolService.updateToolValidation(tool, boschId, reason).subscribe(
            (data: any) => {
                this._logger.info('revalidateTool:: Operation successful');
                this.toastr.success(this.translate.instant('admin.tools.details.toasts.revalidated'));
                this.getToolById(this.selectedTool.id);
                this.revalidateScanBadgeModal.close();
            }, (error: any) => {
                this._logger.error('revalidateTool:: Operation failed');
                this.revalidateScanBadgeModal.close();
            });
    }


    onScanForDeletion(event) {
        event.inspected = true;
    }

    isInUse() {
        this._logger.info('isInUse:: Tool is in use', this.selectedTool);
        this.toolService.isInUse(this.selectedTool).subscribe(
            (data) => {
                this.inUseActions = '';
                const keys = Object.keys(data);
                for (let i = 0; i < keys.length; i++) {
                    if (data[keys[i]] === true) {
                        this.inUseActions = this.translationService
                            .instant('admin.tools.details.reset-confirmation-modal.action.' + keys[i]);
                        break;
                    }
                }

                if (this.inUseActions.length > 0) {
                    this.resetConfirmationModal.open();
                } else {
                    this.onResetTool();
                }
                this.resetToolConfirmationModal.close();
            },
            (error: any) => {
                this._logger.error('getToolInUse:: Operation failed', error);
            }
        );
    }

    onResetTool() {
        this.requestInProgress = true;
        this._logger.info('resetTool:: Resetting tool', this.selectedTool);
        this.toolService.resetTool(this.selectedTool).subscribe(
            data => {
                this._logger.info('resetTool:: Operation successful');
                this.toastr.success(this.buildSuccessfulResetMessage());
                this.isToolScanned = false;
                this.getToolById(this.selectedTool.id);
                this.resetConfirmationModal.close();
                this.requestInProgress = false;
            },
            error => {
                this._logger.error('resetTool:: Operation successful', error);
                this.requestInProgress = false;
                this.resetConfirmationModal.close();
            });
    }

    onRowAlertClick(node: TreeModel) {
        this._logger.info("onRowAlertClick:: event", node);
        this.issueModal.open(this.selectedTool.issue, node.data.id, true);
    }

    onModalClose(event) {
        this.getToolById(this.selectedToolId);
    }

    shouldRenderInvalidate() {
        return this.selectedTool && this.selectedTool.isAvailableForManufactoring && this.selectedTool.isInStorage;
    }

    shouldRenderValidate() {
        return !this.shouldRenderInvalidate() && this.areAllToolsEngraved() &&
            this.selectedTool.notAvailableReason === 3 && this.selectedTool.isInStorage;
    }

    areAllToolsEngraved() {
        let are = true;

        if (!this.toolDatamatrix) {
            are = false;
        } else {
            this.toolDatamatrix.forEach(toolDatamatrix => {
                if (!toolDatamatrix.isEngraved) {
                    are = false;
                }
            });
        }

        return are;
    }

    openToolEngraveModal() {
        this.engraveModal.open();
    }

    openConfirmDeleteToolModal() {
        this.scannedTools = [this.selectedTool];
        this.deleteToolConfirmationModal.open();
    }

    openResetToolStatusAndStanceModal() {
        this.scannedTools = [this.selectedTool];
        this.resetToolConfirmationModal.open();
    }

    openToolBackupModal() {
        this.getToolBackups(this.selectedTool.id);
        this.toolBackupModal.open();
    }

    shouldRenderModifiers(selectedTool: Tool) {
        return (this.canManageTools() || this.canResetTools())
            && selectedTool && !selectedTool.isDeleted && !this.loading;
    }

    onRegenerateDataMatrix() {
        this.getToolDatamatrix(this.selectedTool.id);
        this.engraveModal.open();
    }

    confirmInvalidateTool(reason: string, boschId: string) {
        this._logger.info('invalidateTool:: Invalidating tool', this.selectedTool);
        this.requestInProgress = true;
        this.selectedTool.isAvailableForManufactoring = !this.selectedTool.isAvailableForManufactoring;
        this.toolService.updateToolValidation(this.selectedTool, boschId, reason).subscribe(
            (data: any) => {
                this._logger.info('invalidateTool:: Operation successful');
                this.toastr.success(this.translate.instant('admin.tools.details.toasts.invalidated'));

                this.requestInProgress = false;
                this.invalidateScanBadgeModal.close()

                this.getToolById(this.selectedTool.id);
            }, (error: any) => {
                this._logger.error('invalidateTool:: Operation failed');
                this.requestInProgress = false;
                this.invalidateScanBadgeModal.close()
            });
    }

    private getToolDatamatrix(toolId) {
        this._logger.info('getToolDatamatrix:: Retrieving datamatrixes for toolId', toolId);
        this.toolService.getToolDatamatrix(toolId).subscribe(toolDM => {
            this._logger.info('getToolDatamatrix:: API Returned data', toolDM);
            this.toolDatamatrix = toolDM;

            this.fabItems = this.computeFABItems();
        });
    }

    private buildCreateMessage(ticketId) {
        const message = this.translationService.instant('admin.tickets.toasts.created');
        return this.translateParser.interpolate(message, {ticketId: ticketId});
    }

    private buildDeleteToolSuccessMessage() {
        return this.translate.instant('admin.tools.toasts.deleted',
            {toolName: this.selectedTool.name});
    }

    private buildSuccessfulResetMessage() {
        return this.translate.instant('admin.tools.toasts.reset',
            {toolName: this.selectedTool.name});
    }

    onSaveBackups(backupData: any) {
        this._logger.info('onCheckedTools:: Updating tool backups based on selections', backupData.backupIds);
        this.requestInProgress = true;

        this.toolService.updateTool({
            id: backupData.selectedToolId,
            backupTools: backupData.backupIds
        }).subscribe((data) => {
            this._logger.info('onCheckedTools:: API returned data', data);
            this.toastr.success(this.translationService.instant('admin.tools.toasts.backupSuccess'));
            this.requestInProgress = false;
            this.toolBackupModal.close();
        }, (error) => {
            this._logger.info('onCheckedTools:: API returned error', error);
            this.requestInProgress = false;
            this.toastr.error(this.translationService.instant('admin.tools.toasts.backupFail'));
        });
    }

    computeFABItems() {
        const items = [];
        if (this.canManageTools() && this.shouldRenderInvalidate()) {
            items.push({
                tooltipOptions: {
                    tooltipLabel: this.translate.instant('admin.tools.details.header.buttons.invalidate'),
                    tooltipPosition: 'left'
                },
                color: '#C62828',
                icon: 'fa fa-minus-circle',
                label: this.translate.instant('admin.tools.details.header.buttons.invalidate'),
                command: () => {
                    this.invalidateTool(this.selectedTool);
                }
            });
        }

        if (this.canManageTools() && this.shouldRenderValidate()) {
            items.push({
                tooltipOptions: {
                    tooltipLabel: this.translate.instant('admin.tools.details.header.buttons.revalidate'),
                    tooltipPosition: 'left'
                },
                color: '#439FB2',
                icon: 'fa fa-check-square',
                command: () => {
                    this.revalidateTool(this.selectedTool);
                }
            });
        }

        if (this.canManageTools()) {
            items.push({
                tooltipOptions: {
                    tooltipLabel: this.translate.instant('admin.tools.details.header.buttons.engrave'),
                    tooltipPosition: 'left'
                },
                color: '#395692',
                icon: 'fa fa-qrcode',
                command: () => {
                    this.openToolEngraveModal();
                }
            });
        }

        if (this.canResetTools()) {
            items.push({
                tooltipOptions: {
                    tooltipLabel: this.translate.instant('generic.buttons.reset'),
                    tooltipPosition: 'left'
                },
                color: '#FFAB00',
                icon: 'fa fa-undo',
                command: () => {
                    this.openResetToolStatusAndStanceModal();
                }
            });
        }

        if (this.canDeleteTools() && this.selectedTool.isInStorage) {
            items.push({
                tooltipOptions: {
                    tooltipLabel: this.translate.instant('generic.buttons.delete'),
                    tooltipPosition: 'left'
                },
                color: '#C62828',
                icon: 'fa fa-trash-o',
                command: () => {
                    this.openConfirmDeleteToolModal();
                }
            });
        }

        if (this.canManageTools()) {
            items.push({
                tooltipOptions: {
                    tooltipLabel: this.translate.instant('generic.buttons.backups'),
                    tooltipPosition: 'left'
                },
                color: '#6DB74D',
                icon: 'fa fa-cog',
                command: () => {
                    this.openToolBackupModal();
                }
            });
        }

        return items;
    }

    private canManageTools() {
        return this.permissionService.hasHasPermissionForFeature('MANAGE_TOOLS');
    }

    private canResetTools() {
        return this.permissionService.hasHasPermissionForFeature('RESET_TOOLS');
    }

    private canDeleteTools() {
        return this.canManageTools() && this.authenticationService.isCurrentUserWithRole(Role.ADMIN);
    }
}
